import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, inr } from "../lib/api";
import { useSeo } from "../components/site/Seo";
import { ProductCard, SectionHeader } from "../components/site/Cards";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { track } from "../lib/tracking";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck, Clock, FileText, ArrowRight, PlayCircle } from "lucide-react";
import NotFound from "./NotFound";

export default function ProductPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [p, setP] = useState(undefined);

  const load = () => api.get(`/products/${slug}`).then((r) => {
    setP(r.data);
    track("ViewContent", { content_type: "product", content_name: r.data.name, value: r.data.price, currency: "INR" });
  }).catch(() => setP(null));

  useEffect(() => { setP(undefined); load(); }, [slug]);
  useSeo({ title: p ? `${p.name} | Agni Shiksha` : "Agni Shiksha", description: p?.short_description });

  if (p === null) return <NotFound />;
  if (!p) return <div className="container-px py-40 text-center text-content-muted">Loading…</div>;

  const off = p.discount || (p.mrp ? Math.round((1 - p.price / p.mrp) * 100) : 0);
  const freeTest = (p.tests || []).find((t) => t.is_free);

  const buy = () => {
    track("InitiateCheckout", { content_name: p.name, value: p.price, currency: "INR" });
    if (!user) { nav(`/login?next=/checkout/${p.slug}`); return; }
    nav(`/checkout/${p.slug}`);
  };

  const startFree = async (t) => {
    if (!user) { nav(`/login?next=/test/${t.id}/instructions`); return; }
    nav(`/test/${t.id}/instructions`);
  };

  return (
    <div className="pb-28 lg:pb-16">
      <section className="hero-glow container-px pt-12 pb-10">
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <Link to={`/${p.exam_slug}`} className="text-xs uppercase tracking-widest text-ember">{p.type}</Link>
            <h1 className="font-heading text-3xl lg:text-4xl font-extrabold tracking-tight mt-3 leading-tight">{p.name}</h1>
            <p className="text-content-secondary text-lg mt-4 leading-relaxed">{p.description}</p>
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-6 text-sm text-content-secondary">
              {p.num_tests ? <span className="flex items-center gap-1.5"><FileText className="w-4 h-4 text-ember" /> {p.num_tests}+ tests</span> : null}
              {p.num_questions ? <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-ember" /> {p.num_questions.toLocaleString("en-IN")}+ MCQs</span> : null}
              {p.validity_days ? <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-ember" /> {Math.round(p.validity_days / 30)} months validity</span> : null}
            </div>

            {(p.tests || []).length > 0 && (
              <div className="mt-10">
                <h3 className="font-heading font-semibold mb-4">Included tests</h3>
                <div className="space-y-2">
                  {p.tests.map((t) => (
                    <div key={t.id} className="card-surface p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <FileText className="w-4 h-4 text-content-muted" />
                        <div>
                          <div className="text-sm font-medium">{t.name}</div>
                          <div className="text-xs text-content-muted">{t.total_questions} questions • {t.duration_minutes} min</div>
                        </div>
                      </div>
                      {t.is_free ? (
                        <Button size="sm" variant="outline" className="rounded-full border-ember/50 text-ember" onClick={() => startFree(t)} data-testid={`free-test-${t.id}`}>
                          <PlayCircle className="w-4 h-4 mr-1" /> Free
                        </Button>
                      ) : p.owned ? (
                        <Button size="sm" className="btn-ember rounded-full" onClick={() => startFree(t)}>Start</Button>
                      ) : (
                        <span className="text-xs text-content-muted flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Locked</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Buy box */}
          <div className="lg:col-span-1">
            <div className="card-surface p-6 lg:sticky lg:top-24">
              {p.price === 0 ? (
                <div className="font-heading font-bold text-3xl text-emerald-400 mb-4">Free</div>
              ) : (
                <div className="flex items-baseline gap-3 mb-1">
                  <span className="font-heading font-extrabold text-3xl">{inr(p.price)}</span>
                  {p.mrp > p.price && <span className="text-content-muted line-through">{inr(p.mrp)}</span>}
                  {off > 0 && <span className="text-sm font-semibold text-emerald-400">{off}% off</span>}
                </div>
              )}
              {p.gst ? <p className="text-xs text-content-muted mb-4">+{p.gst}% GST at checkout</p> : <div className="mb-4" />}

              {p.owned ? (
                <Button asChild className="btn-ember rounded-full w-full h-11"><Link to={`/student/series/${p.id}`}>Go to test series <ArrowRight className="w-4 h-4 ml-1" /></Link></Button>
              ) : p.price === 0 ? (
                freeTest ? <Button className="btn-ember rounded-full w-full h-11" onClick={() => startFree(freeTest)}>Start free test</Button>
                  : <Button disabled className="rounded-full w-full h-11">Coming soon</Button>
              ) : (
                <Button className="btn-ember rounded-full w-full h-11" onClick={buy} data-testid="product-buy-now">Buy Now <ArrowRight className="w-4 h-4 ml-1" /></Button>
              )}

              {freeTest && !p.owned && p.price > 0 && (
                <Button variant="outline" className="rounded-full w-full h-11 mt-3 border-line" onClick={() => startFree(freeTest)}>
                  <PlayCircle className="w-4 h-4 mr-1" /> Try free demo
                </Button>
              )}
              <ul className="mt-6 space-y-2.5 text-sm">
                {["Full access on all devices", "All India Rank & percentile", "Detailed solutions & analysis", "Bilingual (English & Hindi)"].map((t) => (
                  <li key={t} className="flex items-center gap-2 text-content-secondary"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> {t}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {(p.related || []).length > 0 && (
        <section className="container-px section-y">
          <SectionHeader eyebrow="You may also like" title="Related test series" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {p.related.map((r) => <ProductCard key={r.id} p={r} />)}
          </div>
        </section>
      )}

      {/* sticky mobile buy */}
      {!p.owned && p.price > 0 && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 glass-nav border-t border-line px-4 py-3 flex items-center justify-between">
          <div className="font-heading font-bold text-lg">{inr(p.price)}</div>
          <Button className="btn-ember rounded-full px-6" onClick={buy}>Buy Now</Button>
        </div>
      )}
    </div>
  );
}
